import { validationLib } from "@agentcall/core/lib"
import { roomType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { EchoesEditGuard } from "@/components/echoes/EchoesEditGuard"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLabel,
} from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"

export const roomFormSchema = z.object({
  name: roomType.nameZ
    .min(1, { message: "部屋名を入力してください" })
    .regex(validationLib.ALPHANUMERIC_REGEX, { message: validationLib.ALPHANUMERIC_ERROR_MESSAGE }),
  layout: roomType.layoutZ.optional(),
  size: roomType.sizeZ.optional(),
  floor: z
    .string()
    .min(1, "階数を入力してください")
    .refine((val) => /^\d+$/.test(val) && Number(val) >= 1, "階数は1以上の整数で入力してください"),
})

export type RoomFormValues = z.infer<typeof roomFormSchema>

interface RoomFormProps {
  defaultValues?: Partial<RoomFormValues>
  onSubmit: (values: RoomFormValues) => Promise<void>
  onCancel: () => void
  isEdit?: boolean
  isSubmitting: boolean
}

export function RoomForm({
  defaultValues,
  onSubmit,
  onCancel,
  isEdit = false,
  isSubmitting,
}: RoomFormProps) {
  const form = useForm<RoomFormValues>({
    resolver: zodResolver(roomFormSchema),
    defaultValues,
  })

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(async (values) => {
          await onSubmit(values)
        })}
        className="space-y-5"
      >
        <Card>
          <CardContent className="space-y-4 p-[15px]">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1 font-semibold text-sm text-slate-600">
                    部屋名
                    <FormRequiredLabel />
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="102" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-2.5">
              <FormField
                control={form.control}
                name="layout"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="flex items-center gap-1 font-semibold text-sm text-slate-600">
                      間取り
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="1K" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="size"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="flex items-center gap-1 font-semibold text-sm text-slate-600">
                      広さ（面積）
                    </FormLabel>
                    <FormControl>
                      <div className="flex items-end gap-1.5">
                        <Input
                          placeholder="25"
                          type="number"
                          step="0.01"
                          value={field.value === undefined ? "" : String(field.value)}
                          onChange={(event) => {
                            const { value } = event.target
                            field.onChange(value === "" ? undefined : Number(value))
                          }}
                          className="flex-1"
                        />
                        <span className="text-sm font-semibold text-slate-600 pb-2">㎡</span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="floor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1 font-semibold text-sm text-slate-600">
                    階数
                    <FormRequiredLabel />
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="1"
                      type="number"
                      min={1}
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, "")
                        field.onChange(value)
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2.5">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="font-bold text-primary"
          >
            キャンセル
          </Button>
          <EchoesEditGuard>
            <Button type="submit" loading={isSubmitting}>
              {isEdit ? "更新する" : "登録する"}
            </Button>
          </EchoesEditGuard>
        </div>
      </form>
    </Form>
  )
}
